import { dayNumber, monthOf } from '../core/time';
import type { DayNumber } from '../core/time';
import type { SoilKey } from '../data/types';

/** 基準日（実績を最後に確認した日）と、表示できる期間。 */
export const BASE_DATE = '2026-10-06';
export const B: DayNumber = dayNumber(BASE_DATE);
export const T_MIN: DayNumber = dayNumber('2025-06-01');
export const T_MAX: DayNumber = dayNumber('2028-09-30');

/** 土質と土量変化率（サンプル値）。L＝ほぐし÷地山、C＝締固め÷地山。 */
export interface Soil {
  name: string;
  L: number;
  C: number;
}
export const SOILS: Readonly<Record<SoilKey, Soil>> = {
  sand: { name: '砂質土', L: 1.2, C: 0.9 },
  clay: { name: '粘性土', L: 1.3, C: 0.9 },
  gravel: { name: '礫質土', L: 1.2, C: 0.95 },
  rock: { name: '軟岩', L: 1.4, C: 1.1 },
};

/** 地域係数（都道府県別・サンプル値）。 */
export interface Region {
  /** 雨天・休止率（%）。 */
  rain: number;
  /** 冬期の開始月と終了月。0 は冬期なし。 */
  ws: number;
  we: number;
  /** 冬期の土工稼働率（0〜1）。 */
  wf: number;
  /** 残土処分場までの標準距離（km）。 */
  dExt: number;
}

// [雨天・休止率%, 冬期の開始月, 終了月, 冬期の土工稼働率, 残土の標準距離km]
const REGION_RAW: Record<string, [number, number, number, number, number]> = {
  北海道: [10, 12, 3, 0.3, 10],
  青森県: [14, 12, 3, 0.5, 8],
  岩手県: [12, 12, 2, 0.6, 8],
  宮城県: [11, 1, 2, 0.8, 10],
  秋田県: [15, 12, 3, 0.5, 8],
  山形県: [14, 12, 3, 0.5, 8],
  福島県: [12, 1, 2, 0.7, 9],
  茨城県: [12, 0, 0, 1, 12],
  栃木県: [13, 0, 0, 1, 10],
  群馬県: [11, 0, 0, 1, 10],
  埼玉県: [12, 0, 0, 1, 15],
  千葉県: [13, 0, 0, 1, 15],
  東京都: [13, 0, 0, 1, 25],
  神奈川県: [14, 0, 0, 1, 20],
  新潟県: [16, 12, 2, 0.5, 10],
  富山県: [18, 12, 2, 0.5, 9],
  石川県: [18, 12, 2, 0.5, 9],
  福井県: [17, 12, 2, 0.5, 9],
  山梨県: [11, 0, 0, 1, 9],
  長野県: [10, 12, 2, 0.7, 9],
  岐阜県: [15, 1, 2, 0.8, 9],
  静岡県: [15, 0, 0, 1, 12],
  愛知県: [13, 0, 0, 1, 15],
  三重県: [14, 0, 0, 1, 10],
  滋賀県: [13, 1, 2, 0.9, 10],
  京都府: [13, 0, 0, 1, 15],
  大阪府: [12, 0, 0, 1, 20],
  兵庫県: [12, 0, 0, 1, 15],
  奈良県: [12, 0, 0, 1, 12],
  和歌山県: [13, 0, 0, 1, 10],
  鳥取県: [16, 1, 2, 0.7, 8],
  島根県: [16, 1, 2, 0.8, 8],
  岡山県: [10, 0, 0, 1, 10],
  広島県: [12, 0, 0, 1, 12],
  山口県: [14, 0, 0, 1, 9],
  徳島県: [13, 0, 0, 1, 9],
  香川県: [10, 0, 0, 1, 9],
  愛媛県: [12, 0, 0, 1, 9],
  高知県: [17, 0, 0, 1, 9],
  福岡県: [14, 0, 0, 1, 15],
  佐賀県: [15, 0, 0, 1, 9],
  長崎県: [15, 0, 0, 1, 10],
  熊本県: [16, 0, 0, 1, 10],
  大分県: [14, 0, 0, 1, 9],
  宮崎県: [17, 0, 0, 1, 9],
  鹿児島県: [17, 0, 0, 1, 10],
  沖縄県: [15, 0, 0, 1, 12],
};

export const REGIONS: Readonly<Record<string, Region>> = Object.fromEntries(
  Object.entries(REGION_RAW).map(([k, [rain, ws, we, wf, dExt]]) => [
    k,
    { rain, ws, we, wf, dExt },
  ]),
);

/** 47 都道府県の名前（地域係数を持つ県）。 */
export const PREF_NAMES: ReadonlySet<string> = new Set(Object.keys(REGION_RAW));

/** その日が冬期か。月単位で判定し、開始月 > 終了月なら年をまたぐ。 */
export function inWinter(r: Region, d: DayNumber): boolean {
  if (!r.ws) return false;
  const m = monthOf(d);
  return r.ws <= r.we ? m >= r.ws && m <= r.we : m >= r.ws || m <= r.we;
}
